<script lang="ts" setup>
import { computed, onUnmounted, reactive, ref, watch } from 'vue'
import type { HomePageConfig } from '../configs/types'
import ModuleBannerSlider from './modules/ModuleBannerSlider.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.vue'
import ModuleFeed from './modules/ModuleFeed.vue'
import ModuleRankingBig from './modules/ModuleRankingBig.vue'
import ModuleBookSwiper from './modules/ModuleBookSwiper.vue'
import CategoryPanel from './CategoryPanel.vue'
import TagsPanel from './TagsPanel.vue'
import { COLUMN_TYPE } from '@/constants'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  homeBgUrl: any
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

// 定义 emit 以保持与 default 组件的类型兼容性
defineEmits<{
  (e: 'update:input', value: string): void
}>()

// 当前页签类型：cms(Discover) / category / tags
const navType = computed<string>(() => props.pageInfo?.navType || 'cms')

// Category/Tags 面板懒挂载并保活（v-show 切换，状态不丢）
const mountedPanels = reactive({ category: false, tags: false })
watch(navType, (t) => {
  if (t === 'category')
    mountedPanels.category = true
  if (t === 'tags')
    mountedPanels.tags = true
}, { immediate: true })

// 获取 BannerSlider 模块（SLIDE_BOOK 类型）
const bannerModule = computed(() => {
  return props.modules.find(col => col.columnType === COLUMN_TYPE.SLIDE_BOOK)
})

// 获取其他模块（排除 Banner；标签模块已有独立 Tags 页签，Discover 不再展示）
const otherModules = computed(() => {
  return props.modules.filter(col =>
    col.columnType !== COLUMN_TYPE.SLIDE_BOOK
    && col.columnType !== COLUMN_TYPE.ROW_2_MUL_COL_3,
  )
})

const scrollContainer = ref<HTMLElement | null>(null)

// 设置滚动容器引用（透传给 useInfiniteScroll）
function setScrollRef(el: HTMLElement | null) {
  scrollContainer.value = el
  props.setInfiniteRef(el)
}

onUnmounted(() => {
  scrollContainer.value = null
})
</script>

<template>
  <div class="home-root h-100dvh flex flex-col bg-n-bg-global">
    <!-- 固定头部：品牌行 + 页签行 -->
    <div class="home-header shrink-0">
      <!-- 品牌行：渐变 Logo + 钻石胶囊 -->
      <div class="brand-row flex items-center justify-between px-15px">
        <img
          src="/images/vowpath/home/logo.png"
          class="brand-logo"
          alt="Vowpath"
        >
        <CoinEntry />
      </div>
      <!-- 页签行 + 搜索图标 -->
      <div class="px-15px flex items-center justify-between">
        <div class="flex items-center space-x-24px overflow-x-auto flex-1 tab-scroll">
          <div v-for="item in pageList" :key="item.id">
            <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
              <span>{{ item.showName }}</span>
              <i v-if="item.id === pageInfo.id" class="tab-underline" />
            </div>
          </div>
        </div>
        <div class="ml-12px shrink-0 cursor-pointer flex items-center justify-center" @click="onSearch">
          <img
            src="/images/vowpath/home/icon-search-white.png"
            width="20px"
            height="20px"
          >
        </div>
      </div>
    </div>

    <!-- 内容区 -->
    <div class="flex-1 min-h-0 relative">
      <!-- Discover：CMS 模块流 -->
      <div v-show="navType === 'cms'" :ref="setScrollRef" class="h-full overflow-y-scroll discover-scroll">
        <div v-if="skeleton" class="h-full">
          <div class="pt-20vh flex justify-center">
            <BasicSpinner class="mx-auto" />
          </div>
          <p class="mt-4 text-center font-bold text-n-text-1">
            {{ config.texts.loading }}
          </p>
        </div>
        <div v-else class="pb-90px">
          <!-- Banner -->
          <div v-if="bannerModule" class="pt-10px">
            <ModuleBannerSlider
              :module="bannerModule"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
              card-class="p-0"
            />
          </div>

          <!-- 其他模块 -->
          <div v-for="col in otherModules" :key="col.id" class="mt-20px">
            <template v-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
              <ModuleFeatured1Plus4
                card-class="p-0 px-15px"
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
              <ModuleGrid2x4
                card-class="p-0 px-15px"
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
              <ModuleGrid1x4
                card-class="p-0 px-15px"
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
              <ModuleRankingBig :module="col" :config="config" card-class="p-0 px-15px" class="w-full" />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
              <ModuleBookSwiper :module="col" :config="config" card-class="p-0 px-10px" class="w-full" />
            </template>
          </div>

          <!-- Feed -->
          <ModuleFeed
            v-if="feeds.length"
            card-class="p-0 px-15px mt-20px"
            :feeds="feeds"
            :feed-show-name="feedShowName"
            :feed-id="feedId"
            :feeds-column-type="feedsColumnType"
            :feed-fetching="feedFetching"
            :feed-ending="feedEnding"
            :config="config"
            :goto-book="gotoBook"
          />
        </div>
      </div>

      <!-- Category：左分类 + 右书籍网格 -->
      <CategoryPanel
        v-if="mountedPanels.category"
        v-show="navType === 'category'"
        class="h-full"
        :goto-book="gotoBook"
      />

      <!-- Tags：标签条 + 书籍网格 -->
      <TagsPanel
        v-if="mountedPanels.tags"
        v-show="navType === 'tags'"
        class="h-full"
        :goto-book="gotoBook"
      />
    </div>

    <BasicTabbar />
  </div>
</template>

<style lang="scss" scoped>
/* 品牌行 */
.brand-row {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* Tab 列表禁止滚动条显示 */
.tab-scroll {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.home-tab {
  cursor: pointer;
  padding: 10px 0;

  span {
    font-weight: 500;
    font-size: 14px;
    color: #ffffff;
    opacity: 0.6;
    line-height: 24px;
    white-space: nowrap;
  }
}

.home-tab.active {
  span {
    position: relative;
    font-size: 20px;
    opacity: 1;
    font-weight: bold;
    z-index: 10;
  }

  .tab-underline {
    display: block;
    position: absolute;
    left: 50%;
    bottom: 2px;
    transform: translateX(-50%);
    width: 23px;
    height: 4px;
    background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
    border-radius: 3px;
  }
}

.discover-scroll {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
